import React, { useState, useEffect } from "react";
import {
  Checkbox,
  Text,
  Button,
  DataTable,
  Banner,
  Box,
  InlineStack,
  BlockStack,
  Icon,
} from "@shopify/polaris";
import {
  AlertTriangleIcon,
  ClipboardChecklistIcon,
  ClockIcon,
  LanguageTranslateIcon,
  ShieldCheckMarkIcon,
} from "@shopify/polaris-icons";

const ProductCheckSettings = ({ shop, onSave, initialSettings = {} }) => {
  const safeInitial = initialSettings || {};
  const [settings, setSettings] = useState({
    autoReminder: safeInitial.autoReminder ?? false,
    enabled: safeInitial.enabled ?? true,
    checklist: {
      vendorNameMatch: safeInitial.checklist?.vendorNameMatch ?? true,
      noFullCaps: safeInitial.checklist?.noFullCaps ?? true,
      noGifs: safeInitial.checklist?.noGifs ?? true,
      hasCollections: safeInitial.checklist?.hasCollections ?? true,
      notTaxable: safeInitial.checklist?.notTaxable ?? true,
    },
    forbiddenTerms: safeInitial.forbiddenTerms || {
      NL: [
        "®",
        "TM",
        "Verzekerd verzenden",
        "ortho",
        "pijn verlichtend",
        "sexy",
        "Beperkte voorraad",
        "kasjmier",
        "cashm",
        "Kasch",
        "Tevreden klanten",
        "reviews",
      ],
      DE: [
        "®",
        "TM",
        "Versicherter Versand",
        "ortho",
        "schmerzlindernd",
        "sexy",
        "wenige Stücke auf Lager",
        "kasjmir",
        "cashm",
        "Kasch",
        "zufriedene Kunden",
        "Bewertungen",
      ],
      ENG: [
        "®",
        "TM",
        "insured shipping",
        "ortho",
        "pain relieving",
        "sexy",
        "Limited stock",
        "kasjmir",
        "cashm",
        "cashm",
        "Happy customers",
        "reviews",
      ],
      FR: [
        "®",
        "TM",
        "expédition assurée",
        "ortho",
        "soulager la douleur",
        "sexy",
        "Stock limité",
        "kasjmir",
        "cashm",
        "cashm",
        "clients satisfaits",
        "avis",
      ],
      AUS: [
        "®",
        "TM",
        "insured shipping",
        "ortho",
        "pain relieving",
        "sexy",
        "Limited stock",
        "kasjmir",
        "cashm",
        "cashm",
        "Happy customers",
        "reviews",
      ],
      IT: [
        "®",
        "TM",
        "spedizione assicurata",
        "orto",
        "alleviare il dolore",
        "sexy",
        "Stock limitato",
        "kashmir",
        "cashm",
        "cashm",
        "clienti soddisfatti",
        "recensioni",
      ],
      SP: [
        "®",
        "TM",
        "envío asegurado",
        "orto",
        "analgésico",
        "sexy",
        "Existencias limitadas",
        "Kash",
        "cashe",
        "",
        "clientes satisfechos",
        "reseñas",
      ],
      PT: [
        "®",
        "TM",
        "envio com seguro",
        "orto",
        "analgésico",
        "sexy",
        "Stock limitato",
        "kasjmir",
        "cash",
        "Caxemira",
        "clientes satisfeitos",
        "críticas",
      ],
      SE: [
        "®",
        "TM",
        "försäkrad frakt",
        "ortho",
        "smärtlindrande",
        "sexy",
        "Begränsat lager",
        "kasjmir",
        "cashm",
        "cashm",
        "nöjda kunder",
        "recensioner",
      ],
    },
  });

  // Sync state when initialSettings prop changes
  useEffect(() => {
    const si = initialSettings || {};
    setSettings({
      autoReminder: si.autoReminder ?? false,
      enabled: si.enabled ?? true,
      checklist: {
        vendorNameMatch: si.checklist?.vendorNameMatch ?? true,
        noFullCaps: si.checklist?.noFullCaps ?? true,
        noGifs: si.checklist?.noGifs ?? true,
        hasCollections: si.checklist?.hasCollections ?? true,
        notTaxable: si.checklist?.notTaxable ?? true,
      },
      forbiddenTerms: si.forbiddenTerms || settings.forbiddenTerms,
    });
  }, [initialSettings]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleCheckboxChange = (key, value) => {
    if (key === "autoReminder") {
      setSettings((prev) => ({ ...prev, autoReminder: value }));
    } else {
      setSettings((prev) => ({
        ...prev,
        checklist: { ...prev.checklist, [key]: value },
      }));
    }
  };

  const handleForbiddenTermChange = (language, index, value) => {
    setSettings((prev) => ({
      ...prev,
      forbiddenTerms: {
        ...prev.forbiddenTerms,
        [language]: prev.forbiddenTerms[language].map((term, i) =>
          i === index ? value : term
        ),
      },
    }));
  };

  const addForbiddenTerm = (language) => {
    setSettings((prev) => ({
      ...prev,
      forbiddenTerms: {
        ...prev.forbiddenTerms,
        [language]: [...prev.forbiddenTerms[language], ""],
      },
    }));
  };

  const removeForbiddenTerm = (language, index) => {
    setSettings((prev) => ({
      ...prev,
      forbiddenTerms: {
        ...prev.forbiddenTerms,
        [language]: prev.forbiddenTerms[language].filter((_, i) => i !== index),
      },
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await onSave(settings);
      setMessage("Settings saved successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage("Error saving settings. Please try again.");
      setTimeout(() => setMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const forbiddenTermsRows = [
    ["®", "®", "®", "®", "®", "®", "®", "®", "®"],
    ["TM", "TM", "TM", "TM", "TM", "TM", "TM", "TM", "TM"],
    [
      "Verzekerd verzenden",
      "Versicherter Versand",
      "insured shipping",
      "expédition assurée",
      "insured shipping",
      "spedizione assicurata",
      "envío asegurado",
      "envio com seguro",
      "försäkrad frakt",
    ],
    [
      "ortho",
      "ortho",
      "ortho",
      "ortho",
      "ortho",
      "orto",
      "orto",
      "orto",
      "ortho",
    ],
    [
      "pijn verlichtend",
      "schmerzlindernd",
      "pain relieving",
      "soulager la douleur",
      "pain relieving",
      "alleviare il dolore",
      "analgésico",
      "analgésico",
      "smärtlindrande",
    ],
    ["sexy", "sexy", "sexy", "sexy", "sexy", "sexy", "sexy", "sexy", "sexy"],
    [
      "Beperkte voorraad",
      "wenige Stücke auf Lager",
      "Limited stock",
      "Stock limité",
      "Limited stock",
      "Stock limitato",
      "Existencias limitadas",
      "Stock limitato",
      "Begränsat lager",
    ],
    [
      "kasjmier",
      "kasjmir",
      "kasjmir",
      "kasjmir",
      "kasjmir",
      "kashmir",
      "Kash",
      "kasjmir",
      "kasjmir",
    ],
    [
      "cashm",
      "cashm",
      "cashm",
      "cashm",
      "cashm",
      "cashm",
      "cashe",
      "cash",
      "cashm",
    ],
    ["Kasch", "", "", "", "", "", "", "Caxemira", ""],
    [
      "Tevreden klanten",
      "zufriedene Kunden",
      "Happy customers",
      "clients satisfaits",
      "Happy customers",
      "clienti soddisfatti",
      "clientes satisfechos",
      "clientes satisfeitos",
      "nöjda kunder",
    ],
    [
      "reviews",
      "Bewertungen",
      "reviews",
      "avis",
      "reviews",
      "recensioni",
      "reseñas",
      "críticas",
      "recensioner",
    ],
  ];

  const languageHeaders = [
    { key: "NL", label: "NL" },
    { key: "DE", label: "DE" },
    { key: "ENG", label: "ENG" },
    { key: "FR", label: "FR" },
    { key: "AUS", label: "AUS" },
    { key: "IT", label: "IT" },
    { key: "SP", label: "SP" },
    { key: "PT", label: "PT" },
    { key: "SE", label: "SE" },
  ];

  const checklistItems = [
    {
      key: "vendorNameMatch",
      label: "Vendor name matches the store name",
    },
    {
      key: "noFullCaps",
      label: "Product title is not written in FULL CAPS",
    },
    {
      key: "noGifs",
      label: "No GIFs present in product media",
    },
    {
      key: "hasCollections",
      label: "Product is assigned to at least 1 collection",
    },
    {
      key: "notTaxable",
      label: "Product is set to Not Taxable",
    },
  ];

  return (
    <div>
      {message && (
        <Box paddingBlockEnd="400">
          <Banner
            title={message.includes("Error") ? "Error" : "Success"}
            tone={message.includes("Error") ? "critical" : "success"}
            onDismiss={() => setMessage("")}
          >
            <p>{message}</p>
          </Banner>
        </Box>
      )}

      <BlockStack gap="400">
        <div className="app-surface-card">
          <div className="app-surface-card__body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <span className="app-icon-tile">
                <Icon source={ShieldCheckMarkIcon} tone="base" />
              </span>
              <div>
                <Text variant="headingMd" as="h2">
                  Opt-in controls
                </Text>
                <Text as="p" tone="subdued">
                  Turn product validation and upload reminders on or off.
                </Text>
              </div>
            </div>
            <div className="check-grid">
              <label
                className={`check-item${settings.autoReminder ? " is-on" : ""}`}
              >
                <Icon source={ClockIcon} tone="base" />
                <div>
                  <Checkbox
                    label="Automatic reminder if no products uploaded in 7 days"
                    checked={settings.autoReminder}
                    onChange={(value) =>
                      handleCheckboxChange("autoReminder", value)
                    }
                  />
                </div>
              </label>
              <label
                className={`check-item${settings.enabled ? " is-on" : ""}`}
              >
                <Icon source={ClipboardChecklistIcon} tone="base" />
                <div>
                  <Checkbox
                    label="Enable product checks before uploading to GMC"
                    checked={settings.enabled}
                    onChange={(value) =>
                      setSettings((prev) => ({ ...prev, enabled: value }))
                    }
                  />
                </div>
              </label>
            </div>
          </div>
        </div>

        <div className="app-surface-card">
          <div className="app-surface-card__body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <span className="app-icon-tile app-icon-tile--blue">
                <Icon source={ClipboardChecklistIcon} tone="base" />
              </span>
              <div>
                <Text variant="headingMd" as="h2">
                  Quality checklist
                </Text>
                <Text as="p" tone="subdued">
                  Optional rules. Everything is ON by default.
                </Text>
              </div>
            </div>
            <div className="check-grid">
              {checklistItems.map((item) => (
                <label
                  key={item.key}
                  className={`check-item${
                    settings.checklist[item.key] ? " is-on" : ""
                  }`}
                >
                  <Icon source={ShieldCheckMarkIcon} tone="base" />
                  <div>
                    <Checkbox
                      label={item.label}
                      checked={settings.checklist[item.key]}
                      onChange={(value) =>
                        handleCheckboxChange(item.key, value)
                      }
                    />
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="app-surface-card">
          <div className="app-surface-card__body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <span className="app-icon-tile app-icon-tile--warn">
                <Icon source={AlertTriangleIcon} tone="base" />
              </span>
              <div>
                <Text variant="headingMd" as="h2">
                  Forbidden terms
                </Text>
                <Text as="p" tone="subdued">
                  Products must not contain these policy-sensitive terms.
                </Text>
              </div>
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Icon source={LanguageTranslateIcon} tone="base" />
              <Text as="span" tone="subdued">
                Multi-language blocked term matrix
              </Text>
            </div>
            <Box padding="300" background="bg-surface-secondary">
              <DataTable
                columnContentTypes={[
                  "text",
                  "text",
                  "text",
                  "text",
                  "text",
                  "text",
                  "text",
                  "text",
                  "text",
                ]}
                headings={languageHeaders.map((h) => h.label)}
                rows={forbiddenTermsRows}
              />
            </Box>
          </div>
        </div>

        <InlineStack align="end">
          <Button primary loading={loading} onClick={handleSave}>
            Save Settings
          </Button>
        </InlineStack>
      </BlockStack>
    </div>
  );
};

export default ProductCheckSettings;
